import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ReportCardView } from '../../lib/report_cards';
import { BrandedShell } from '../components/BrandedShell';
import { MessagesThread } from '../components/MessagesThread';
import { PhotoTimeline } from '../components/PhotoTimeline';
import { ReportCardList } from '../components/ReportCardList';
import { useStayRealtime } from '../lib/useStayRealtime';

/**
 * Sub-spec 01d — public live-stay status page at `/stay/<token>`.
 *
 * Headline differentiator per master arch §3.11. The page is unauthenticated
 * — the `bst_<22>` token in the URL is the auth. The page fetches a single
 * snapshot endpoint and renders:
 *   - hero (pet + facility + status)
 *   - activity timeline (shared notes + intake responses + status changes)
 *   - photo timeline (mounts sub-spec 07's <PhotoTimeline scope="public" />)
 *   - camera embed slot (renders "coming soon" when sub-spec 09 not configured)
 *   - messages thread (read-only via <MessagesThread context="public-stay" />)
 *
 * Realtime: subscribes via `useStayRealtime` with the share token; on each
 * BOOKING_MESSAGE_POSTED / BOOKING_PHOTO_POSTED bump we re-fetch the
 * snapshot and bump the children's `realtimeNonce`.
 */

interface CameraStream {
  id: string;
  name: string;
  kind: 'iframe' | 'hls';
  url: string;
}

interface StaySnapshot {
  bookingId: string;
  type: 'boarding' | 'daycare' | 'grooming' | 'service';
  status: string;
  createdAt: string;
  updatedAt: string;
  pet: { id: string; name: string; species: string; breed: string | null } | null;
  facility: {
    id: string;
    name: string;
    timezone: string | null;
    cameraStreamsJson: string | null;
  } | null;
  typeDetail: Record<string, unknown> | null;
  notes: Array<{ id: string; body: string; createdAt: string }>;
  intakeResponses: Array<{
    questionSlug: string;
    label: string;
    answer: number;
    note: string | null;
    answeredAt: string;
  }>;
  messages: Array<{
    id: string;
    sender: 'customer' | 'staff';
    body: string;
    attachmentFileIds: string[];
    createdAt: string;
    deleted: boolean;
  }>;
  activity: Array<{
    id: string;
    action: string;
    createdAt: string;
    metadata: Record<string, unknown> | null;
  }>;
  /** Pet report cards (migration 0093) — newest first. */
  reportCards?: ReportCardView[];
  branding: { accountName: string };
}

function parseCameraStreams(json: string | null): CameraStream[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (s): s is CameraStream =>
        typeof s === 'object' &&
        s !== null &&
        typeof (s as CameraStream).id === 'string' &&
        typeof (s as CameraStream).url === 'string',
    );
  } catch {
    return [];
  }
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function actionLabel(action: string): string {
  // Activity-log actions are dot-separated machine ids. Translate the common
  // ones to friendly labels.
  const map: Record<string, string> = {
    'booking.checked_in': 'Checked in',
    'booking.checked_out': 'Checked out',
    'booking.completed': 'Stay completed',
    'booking.status.updated': 'Status updated',
    'booking_photo.posted': 'New photo posted',
    'booking.note.shared': 'Note shared',
  };
  return map[action] ?? action.replace(/^booking\./, '').replace(/_/g, ' ');
}

export function LiveStayPage() {
  const { token } = useParams<{ token: string }>();
  const [snapshot, setSnapshot] = useState<StaySnapshot | null>(null);
  const [error, setError] = useState<
    'invalid_token' | 'not_found' | 'expired' | 'load_failed' | null
  >(null);

  const { nonce: realtimeNonce } = useStayRealtime({
    bookingId: snapshot?.bookingId ?? '',
    shareToken: token,
    enabled: !!snapshot?.bookingId,
  });

  useEffect(() => {
    if (!token) {
      setError('invalid_token');
      return;
    }
    fetch(`/api/public/stay/${encodeURIComponent(token)}/snapshot`, {
      credentials: 'omit',
    })
      .then(async (res) => {
        if (res.status === 401) {
          setError('invalid_token');
          return;
        }
        if (res.status === 404) {
          setError('not_found');
          return;
        }
        if (res.status === 410) {
          setError('expired');
          return;
        }
        if (!res.ok) {
          setError('load_failed');
          return;
        }
        const data = (await res.json()) as StaySnapshot;
        setSnapshot(data);
      })
      .catch(() => setError('load_failed'));
  }, [token, realtimeNonce]);

  if (error === 'invalid_token') {
    return (
      <BrandedShell>
        <Centred>
          <h1 className="text-2xl font-medium">Link expired</h1>
          <p className="mt-3 text-muted-foreground">
            This live stay link is no longer valid. Ask the kennel for a fresh link.
          </p>
        </Centred>
      </BrandedShell>
    );
  }

  if (error === 'expired') {
    return (
      <BrandedShell>
        <Centred>
          <h1 className="text-2xl font-medium">Link expired</h1>
          <p className="mt-3 text-muted-foreground">
            This stay was completed more than 30 days ago and the live link is no longer available.
          </p>
        </Centred>
      </BrandedShell>
    );
  }

  if (error === 'not_found') {
    return (
      <BrandedShell>
        <Centred>
          <h1 className="text-2xl font-medium">Stay not found</h1>
          <p className="mt-3 text-muted-foreground">
            We couldn&apos;t find the stay this link refers to.
          </p>
        </Centred>
      </BrandedShell>
    );
  }

  if (error === 'load_failed') {
    return (
      <BrandedShell>
        <Centred>
          <h1 className="text-2xl font-medium">Could not load</h1>
          <p className="mt-3 text-muted-foreground">Please try again in a moment.</p>
        </Centred>
      </BrandedShell>
    );
  }

  if (!snapshot) {
    return (
      <BrandedShell>
        <Centred>
          <div className="h-12 w-12 animate-pulse rounded-full bg-muted/50" />
        </Centred>
      </BrandedShell>
    );
  }

  const cameraStreams = parseCameraStreams(snapshot.facility?.cameraStreamsJson ?? null);

  return (
    <BrandedShell>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Hero snapshot={snapshot} />

        <Section title="Live camera" testId="livestay-cameras">
          {cameraStreams.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="livestay-cameras-empty">
              The kennel has not enabled live cameras for this stay.
            </p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '0.75rem',
              }}
            >
              {cameraStreams.map((stream) => (
                <CameraEmbed key={stream.id} stream={stream} />
              ))}
            </div>
          )}
        </Section>

        <Section title="Photo timeline" testId="livestay-photos">
          {token ? (
            <PhotoTimeline
              bookingId={snapshot.bookingId}
              scope="public"
              shareToken={token}
              realtimeNonce={realtimeNonce}
            />
          ) : null}
        </Section>

        <Section title="Report cards" testId="livestay-report-cards">
          <ReportCardList cards={snapshot.reportCards ?? []} petName={snapshot.pet?.name ?? null} />
        </Section>

        <Section title="Activity" testId="livestay-activity">
          <ActivityFeed snapshot={snapshot} />
        </Section>

        <Section title="Messages" testId="livestay-messages">
          {token ? (
            <MessagesThread
              bookingId={snapshot.bookingId}
              context="public-stay"
              shareToken={token}
              realtimeNonce={realtimeNonce}
            />
          ) : null}
        </Section>

        <footer className="mt-12 text-center text-xs text-muted-foreground">
          <span>Powered by Kennel Platform</span>
        </footer>
      </div>
    </BrandedShell>
  );
}

function Hero({ snapshot }: { snapshot: StaySnapshot }) {
  const dates = (() => {
    const td = snapshot.typeDetail ?? {};
    if (snapshot.type === 'boarding' && typeof td.checkIn === 'string') {
      return `${fmtDate(td.checkIn as string)} → ${typeof td.checkOut === 'string' ? fmtDate(td.checkOut as string) : '?'}`;
    }
    if (snapshot.type === 'daycare' && typeof td.date === 'string') {
      return `Visit on ${fmtDate(td.date as string)}`;
    }
    if (snapshot.type === 'grooming' && typeof td.startAt === 'string') {
      return `Appointment ${fmtDate(td.startAt as string)}`;
    }
    return null;
  })();

  return (
    <header
      className="mb-8 rounded-lg border border-border bg-card p-6"
      data-testid="livestay-hero"
    >
      <h1 className="text-3xl font-medium tracking-tight">{snapshot.pet?.name ?? 'Your pet'}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {snapshot.facility?.name ? `at ${snapshot.facility.name}` : null}
        {snapshot.facility?.name ? ' — ' : null}
        <span data-testid="livestay-status">{snapshot.status.replace(/_/g, ' ')}</span>
      </p>
      {dates && <p className="mt-2 text-xs text-muted-foreground num">{dates}</p>}
      {snapshot.status === 'checked_in' && <Countdown snapshot={snapshot} />}
    </header>
  );
}

function Countdown({ snapshot }: { snapshot: StaySnapshot }) {
  const target = (() => {
    const td = snapshot.typeDetail ?? {};
    if (snapshot.type === 'boarding' && typeof td.checkOut === 'string') {
      return new Date(td.checkOut as string).getTime();
    }
    if (snapshot.type === 'daycare' && typeof td.date === 'string') {
      return new Date(td.date as string).getTime();
    }
    return null;
  })();
  if (!target) return null;
  const ms = target - Date.now();
  if (ms <= 0) return null;
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const days = Math.floor(hours / 24);
  const label = days >= 2 ? `${days} days` : `${hours} hours`;
  return (
    <p className="mt-3 text-xs text-muted-foreground" data-testid="livestay-countdown">
      <span className="num">{label}</span> until pickup.
    </p>
  );
}

function ActivityFeed({ snapshot }: { snapshot: StaySnapshot }) {
  const rows = [
    ...snapshot.activity.map((a) => ({
      id: `act-${a.id}`,
      label: actionLabel(a.action),
      detail: null as string | null,
      at: a.createdAt,
    })),
    ...snapshot.notes.map((n) => ({
      id: `note-${n.id}`,
      label: 'Note from the kennel',
      detail: n.body,
      at: n.createdAt,
    })),
    ...snapshot.intakeResponses.map((r) => ({
      id: `intake-${r.questionSlug}`,
      label: r.label,
      detail: r.note ?? (r.answer === 1 ? 'Yes' : 'No'),
      at: r.answeredAt,
    })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="livestay-activity-empty">
        No activity recorded yet.
      </p>
    );
  }
  return (
    <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
      {rows.map((row) => (
        <li
          key={row.id}
          data-testid={`activity-${row.id}`}
          style={{
            borderLeft: '2px solid var(--border, oklch(0.91 0.005 80))',
            paddingLeft: '0.7rem',
          }}
        >
          <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{row.label}</div>
          {row.detail && (
            <div
              style={{ fontSize: '0.8rem', color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
            >
              {row.detail}
            </div>
          )}
          <div
            className="num"
            style={{ fontSize: '0.7rem', color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
          >
            {fmtDate(row.at)}
          </div>
        </li>
      ))}
    </ul>
  );
}

function CameraEmbed({ stream }: { stream: CameraStream }) {
  if (stream.kind !== 'iframe') {
    return (
      <div
        className="rounded-lg border border-border p-4 text-sm text-muted-foreground"
        data-testid={`camera-${stream.id}-unsupported`}
      >
        Stream "{stream.name}" uses an unsupported protocol.
      </div>
    );
  }
  return (
    <div
      data-testid={`camera-${stream.id}`}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        background: 'oklch(0.10 0 0)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <iframe
        title={stream.name}
        src={stream.url}
        allow="autoplay; encrypted-media; picture-in-picture"
        style={{ border: 0, width: '100%', height: '100%' }}
      />
    </div>
  );
}

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6 rounded-lg border border-border p-6" data-testid={testId}>
      <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-[60dvh] max-w-2xl flex-col items-center justify-center px-6 text-center">
      {children}
    </div>
  );
}
